import {Marker, useMapsLibrary} from '@vis.gl/react-google-maps';
import {Show} from 'platform/foundation';
import {useTheme} from 'styled-components';

import {head, last} from 'ramda';

import {RoutePoint} from '../utils/getRouteBounds';
import {
  getRouteMarkerIconUrl,
  RouteMarkerColors,
  routeMarkerGeometry,
  RouteMarkerKind,
} from '../utils/getRouteMarkerIconUrl';

const START_Z_INDEX = 1;
// The finish lies above the start, so a drive that ends where it started shows the flag.
const FINISH_Z_INDEX = 2;
const HEAD_Z_INDEX = 3;

type RouteMarkersProps = {
  path: RoutePoint[];
  /** The part of the route drawn so far, empty before the drawing starts. */
  drawnPath: RoutePoint[];
  isFinished: boolean;
};

/**
 * The start, the moving head while the route is being drawn, and the finish flag once it is done.
 */
export function RouteMarkers(props: RouteMarkersProps) {
  const theme = useTheme();
  // Icon anchors and sizes are Maps API classes, so the markers wait for the core library.
  const core = useMapsLibrary('core');

  const colors: RouteMarkerColors = {
    start: theme.colors.severity.success,
    head: theme.colors.general.accent,
    ink: theme.colors.palettes.neutral[900][100],
    outline: theme.colors.general.white,
  };

  const getIcon = (kind: RouteMarkerKind) => {
    const geometry = routeMarkerGeometry[kind];
    return core
      ? {
          url: getRouteMarkerIconUrl(kind, colors),
          scaledSize: new core.Size(geometry.width, geometry.height),
          anchor: new core.Point(geometry.anchorX, geometry.anchorY),
        }
      : undefined;
  };

  return (
    <Show when={core}>
      <Show when={head(props.drawnPath)}>
        {(position) => (
          <Marker
            position={position}
            icon={getIcon('start')}
            clickable={false}
            zIndex={START_Z_INDEX}
          />
        )}
      </Show>
      <Show when={props.isFinished ? null : last(props.drawnPath)}>
        {(position) => (
          <Marker
            position={position}
            icon={getIcon('head')}
            clickable={false}
            zIndex={HEAD_Z_INDEX}
          />
        )}
      </Show>
      <Show when={props.isFinished ? last(props.path) : null}>
        {(position) => (
          <Marker
            position={position}
            icon={getIcon('finish')}
            clickable={false}
            zIndex={FINISH_Z_INDEX}
          />
        )}
      </Show>
    </Show>
  );
}
